import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Form,
  Input,
  message,
  Select,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DiagramImage } from '../../components/DiagramImage';
import { useTranslation } from 'react-i18next';
import { Create, useForm } from '@refinedev/antd';
import { debounce } from 'lodash';
import qs from 'qs';
import {
  useCustom,
  useCustomMutation,
  useGetLocale,
  useWarnAboutChange,
} from '@refinedev/core';
import { SupportingCycle } from '../../schema/be-graphql-generated';
import * as Sentry from '@sentry/react';
import {
  generatePlantUmlText,
  planCycleCommands,
  type CycleEditOp,
} from '@cc/utils';

import { useClientOptions } from '../../hooks/client-options';
import { TherapistActivityReactContext } from '../../App';
import { useActor } from '@xstate/react';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import { dbCols } from '@cc/schema';
import useStyles from './styles.style';
import { useLocation } from 'react-router-dom';
import { SelectClient } from '../../components/SelectClient';

const { Title } = Typography;

export const SupportingCyclesCreate = (props: any) => {
  const [umlText, setUmlText] = useState('');

  const { t } = useTranslation();
  const locale = useGetLocale();
  const currentLocale = locale();

  const { styles } = useStyles();

  const location = useLocation();
  const { setWarnWhen } = useWarnAboutChange();

  const { clientsLoading } = useClientOptions();

  const therapistActivityService = useContext(TherapistActivityReactContext);
  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );

  const { clientId: locationClientId, cycleId: locationCycleId } =
    useMemo(
      () =>
        qs.parse(
          location.search.charAt(0) === '?'
            ? location.search.slice(1)
            : location.search,
        ),
      [location.search],
    ) || {};

  const [clientId, setClientId] = useState<string | undefined>(
    // E57 (Q-e57g1-2 A rule 4): a create page opened with ?clientId= (Create on a client's tool tab, Use a tool) keeps
    // THAT client; the header bar's filter is the fallback
    (locationClientId as string) ||
      therapistActivityState.context.filterClientId,
  );

  const [cycleId, setCycleId] = useState(locationCycleId as string | undefined);

  // ── E12 · per-edit event sourcing for an ES client ─────────────────────────────────────────
  // The portal routes by the CLIENT's mode (plan 07 C3): an `es` client in a READY domain saves
  // through the per-edit commands (create, then rename / add / remove / toggle / edit — founder G1
  // 2026-08-29, D-per-edit); everyone else keeps the shipped upsert. The editor records WHAT THE
  // THERAPIST DID as an edit script and flushes it on Save (`planCycleCommands`, @cc/utils).
  const [esViciousCycleId, setEsViciousCycleId] = useState<string | null>(null);
  const opsRef = useRef<CycleEditOp[]>([]);
  const loadedBaseRef = useRef<{
    name: string;
    thoughts: { thought: string; edges: boolean[] }[];
  } | null>(null);
  // antd's Form.List `add`/`remove` and `setFieldsValue` fire `onValuesChange` for the WHOLE list
  // (found by the UI spec: one Add click recorded 35 phantom edits/toggles mirroring the graph,
  // including cells of the column that did not exist yet). Programmatic mutations are therefore
  // recorded by INTENT at their handler, with `onValuesChange` recording suppressed meanwhile.
  const suppressRecordingRef = useRef(false);
  const recordOp = useCallback((op: CycleEditOp) => {
    if (suppressRecordingRef.current) return;
    opsRef.current.push(op);
  }, []);
  const withoutRecording = useCallback((fn: () => void, settleMs = 50) => {
    suppressRecordingRef.current = true;
    try {
      fn();
    } finally {
      // the list's own change notifications arrive on the next ticks
      setTimeout(() => {
        suppressRecordingRef.current = false;
      }, settleMs);
    }
  }, []);
  // the row we saved last: its re-fetch must NOT reset the edit script, because the therapist may
  // already be editing again by the time it lands (found by the UI spec — a second save flushed
  // nothing). Only a DIFFERENT row loading is a new baseline.
  const lastSavedRowIdRef = useRef<string | null>(null);
  const normalizeThoughts = (
    thoughts:
      | ReadonlyArray<{
          thought?: string | null;
          edges?: ReadonlyArray<boolean | null | undefined> | null;
        } | null>
      | null
      | undefined,
  ) =>
    (thoughts ?? []).map((th) => ({
      thought: (th?.thought ?? '').trim(),
      edges: [...(th?.edges ?? [])].map(Boolean),
    }));

  // The era is a SERVER fact that can change while this page is open (a flip lands mid-session —
  // HANDOFF T112, found in prod 2026-09-02): both signal queries are asked fresh on every mount and
  // re-asked when the server refuses a legacy write with ES_RESEND_AS_ES (below).
  const { data: clientEraData, refetch: refetchClientEra } = useCustom<{
    _id: string;
    esMode?: string | null;
  }>({
    url: '',
    method: 'get',
    meta: {
      operation: 'client',
      fields: ['_id', 'esMode'],
      variables: { id: { name: 'id', type: 'ID', value: clientId } },
    },
    queryOptions: {
      enabled: Boolean(clientId),
      staleTime: 0,
      refetchOnMount: 'always',
    },
  });
  // the READY set FOR THE CLIENT — a pilot domain is personalised per subject, so asking for the
  // therapist (the caller) would never show a client's pilot
  const { data: readyDomainsData, refetch: refetchReadyDomains } = useCustom<
    string[]
  >({
    url: '',
    method: 'get',
    meta: {
      operation: 'esReadyDomains',
      variables: {
        subject: { name: 'subject', type: 'ID', value: clientId },
      },
    },
    queryOptions: {
      enabled: Boolean(clientId),
      staleTime: 0,
      refetchOnMount: 'always',
    },
  });
  const isEsClient =
    clientEraData?.data?.esMode === 'es' &&
    (
      (readyDomainsData?.data as unknown as string[] | undefined) ?? []
    ).includes('viciousCycle');

  useActivityTracking(cycleId ? cycleId : `${dbCols.supportingCycles}/create`);

  const initialValues = useMemo(
    () => ({
      name: '',
      thoughts: [
        {
          thought: '' as string,
          edges: [false] as boolean[],
        },
      ],
    }),
    [],
  );

  const { formProps, saveButtonProps } = useForm<typeof initialValues>({
    warnWhenUnsavedChanges: true,
  });

  useEffect(() => {
    if (
      clientId &&
      therapistActivityState.context.filterClientId !== clientId
    ) {
      therapistActivitySend({
        type: 'ENTITY_CLIENT_ID_CHANGED',
        clientId: clientId,
      });
    }
  }, [clientId]);

  const onFinish = (values: typeof initialValues) => {
    setUmlText(
      generatePlantUmlText({
        ...values,
        userId: clientId,
      }),
    );
  };

  const {
    data: supportingCycleData,
    isLoading: supportingCycleLoading,
    refetch: refetchSupportingCycle,
    error: supportingCycleError,
  } = useCustom<SupportingCycle>({
    url: '',
    method: 'get',
    meta: {
      operation: 'supportingCycle',
      fields: [
        '_id',
        'name',
        {
          thoughts: ['thought', 'edges'],
        },
        'userId',
        'viciousCycleId',
      ],

      variables: {
        _id: {
          name: '_id',
          type: 'ID',
          value: cycleId,
        },
      },
    },
  });

  useEffect(() => {
    if (supportingCycleData?.data) {
      formProps?.form?.setFieldsValue({
        name: supportingCycleData?.data?.name,
        thoughts: supportingCycleData?.data?.thoughts,
      });

      setClientId(supportingCycleData?.data?.userId || '');
      setEsViciousCycleId(
        (supportingCycleData?.data as { viciousCycleId?: string | null })
          ?.viciousCycleId ?? null,
      );
      if (supportingCycleData?.data?._id !== lastSavedRowIdRef.current) {
        loadedBaseRef.current = {
          name: supportingCycleData?.data?.name ?? '',
          thoughts: normalizeThoughts(supportingCycleData?.data?.thoughts),
        };
        opsRef.current = []; // a different row: the server's row is the new baseline
      }
      // @ts-ignore
      onFinish(formProps?.form?.getFieldsValue());

      if (supportingCycleData?.data?._id) {
        therapistActivitySend({
          type: 'LOADED',
          resource: supportingCycleData?.data?._id,
          clientId: supportingCycleData?.data?.userId || '',
        });
      }
    }
  }, [supportingCycleData]);

  const {
    data: supportingCycleTemplatesData,
    isLoading: supportingCycleTemplatesLoading,
    refetch: refetchSupportingCycleTemplates,
    error: supportingCycleTemplatesError,
  } = useCustom<SupportingCycle[]>({
    url: '',
    method: 'get',
    meta: {
      operation: 'supportingCycles',
      fields: [
        {
          data: [
            '_id',
            'name',
            {
              thoughts: ['thought', 'edges'],
            },
            'templateLanguage',
          ],
        },
        'count',
      ],

      variables: {
        where: {
          name: 'where',
          type: 'JSON',
          value: {
            isTemplate_eq: true,
            templateLanguage_eq: currentLocale || 'en',
          },
        },
      },
    },
  });

  const [availableTemplates, setAvailableTemplates] = useState<
    { label: string; value: string }[]
  >([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  // useEffect to generate templateSelectOptions from supportingCycleTemplatesData
  useEffect(() => {
    if (supportingCycleTemplatesData) {
      // @ts-ignore
      const options = supportingCycleTemplatesData?.data?.data?.map((c) => ({
        label: c.name || '',
        value: c._id,
      }));
      setSelectedTemplate(null);
      setAvailableTemplates(options);
    }
  }, [supportingCycleTemplatesData]);

  const {
    mutateAsync: upsertSupportingCycle,
    data: upsertSupportingCycleData,
    isLoading: upsertSupportingCycleLoading,
    error: upsertSupportingCycleError,
  } = useCustomMutation<{ supportingCycle: SupportingCycle }>();

  const { mutateAsync: esMutate, isLoading: esLoading } =
    useCustomMutation<any>();

  /** One per-edit command over the wire — the payload is the projected legacy row. */
  const sendEsCommand = async (
    operation: string,
    inputType: string,
    value: Record<string, unknown>,
  ) =>
    esMutate({
      url: '',
      method: 'post',
      values: {},
      meta: {
        operation,
        fields: ['viciousCycleId', { supportingCycle: ['_id', 'updatedAt'] }],
        variables: {
          input: { name: 'input', type: inputType, value },
        },
      },
    });

  /**
   * @param identity the row's ES identity to address — defaults to the one the loaded row carried;
   *   the ES_RESEND_AS_ES fallback passes the identity it just re-read from the server, because the
   *   row was learnt before the flip converted it and the state has not caught up yet
   */
  const saveViciousCycleEs = async (
    values: typeof initialValues,
    identity: string | null = esViciousCycleId,
  ) => {
    const draft = {
      name: (values.name ?? '').trim(),
      thoughts: normalizeThoughts(values.thoughts),
    };
    if (!identity) {
      // the FIRST save is one CreateViciousCycle carrying the whole draft (a template clone, or
      // blank) — the board's shape; every later save is per-edit
      const res = await sendEsCommand(
        'createViciousCycle',
        'CreateViciousCycleInput!',
        {
          subject: clientId,
          name: draft.name,
          thoughts: draft.thoughts,
          template: selectedTemplate ?? null,
        },
      );
      const payload = res?.data as
        | { viciousCycleId: string; supportingCycle?: { _id: string } | null }
        | null
        | undefined;
      if (!payload?.supportingCycle?._id) {
        throw new Error('vicious-cycle-save-missing-id'); // a silent null = no permission edge
      }
      setEsViciousCycleId(payload.viciousCycleId);
      lastSavedRowIdRef.current = payload.supportingCycle._id;
      setCycleId(payload.supportingCycle._id);
      therapistActivitySend({
        type: 'CREATED',
        resource: payload.supportingCycle._id,
      });
    } else {
      const commands = planCycleCommands(
        loadedBaseRef.current ?? { name: '', thoughts: [] },
        opsRef.current,
      );
      for (const c of commands) {
        const base = { subject: clientId, viciousCycleId: identity };
        switch (c.kind) {
          case 'rename':
            await sendEsCommand(
              'renameViciousCycle',
              'RenameViciousCycleInput!',
              { ...base, name: c.name },
            );
            break;
          case 'add':
            await sendEsCommand('addCycleThought', 'AddCycleThoughtInput!', {
              ...base,
              thought: c.thought,
            });
            break;
          case 'remove':
            await sendEsCommand(
              'removeCycleThought',
              'RemoveCycleThoughtInput!',
              { ...base, index: c.index },
            );
            break;
          case 'toggle':
            await sendEsCommand(
              'toggleCycleRelation',
              'ToggleCycleRelationInput!',
              { ...base, from: c.from, to: c.to, active: c.active },
            );
            break;
          case 'edit':
            await sendEsCommand('editCycleThought', 'EditCycleThoughtInput!', {
              ...base,
              index: c.index,
              thought: c.thought,
            });
            break;
        }
      }
      if (cycleId) {
        lastSavedRowIdRef.current = cycleId;
        therapistActivitySend({ type: 'UPDATED', resource: cycleId });
      }
    }
    loadedBaseRef.current = draft;
    opsRef.current = [];
    setWarnWhen(false);
    message.success(t('pages.supportingCycle.saveSuccess'));
  };

  const saveSupportingCycle = async () => {
    // @ts-ignore
    const values: typeof initialValues =
      formProps?.form?.getFieldsValue() || {};

    if (!clientId) {
      return;
    }

    if (Object.keys(values).length === 0) {
      return;
    }

    if (isEsClient) {
      try {
        await saveViciousCycleEs(values);
        return;
      } catch (error) {
        // rule 5 (guard-by-server): ES_MODE_MISMATCH means the server routes this client to legacy
        // — fall through to the shipped upsert; anything else is a real failure
        if (!JSON.stringify(error ?? '').includes('ES_MODE_MISMATCH')) {
          message.error(t('pages.supportingCycle.saveError'));
          Sentry.captureException(error);
          return;
        }
      }
    }

    try {
      await upsertSupportingCycle({
        url: '',
        method: 'post',
        values: {},
        meta: {
          operation: 'upsertSupportingCycle',
          fields: ['_id'],
          variables: {
            input: {
              name: 'input',
              type: 'SupportingCycleInput',
              value: {
                data: values,
                where: {
                  clientId,
                  _id: cycleId,
                },
              },
            },
          },
        },
        // the outcome is reported below — never the raw GraphQL error toast
        errorNotification: false,
      });
    } catch (error) {
      // rule 5, the OTHER direction (HANDOFF T112): ES_RESEND_AS_ES means this client is already on
      // the ES path for the domain and the era this page loaded with is stale (a flip landed
      // mid-session). The edit script is recorded in both eras, so: refresh the era, re-read the
      // row's identity (the flip converted it), and re-issue the write as the per-edit commands.
      if (!JSON.stringify(error ?? '').includes('ES_RESEND_AS_ES')) {
        message.error(t('pages.supportingCycle.saveError'));
        Sentry.captureException(error);
        return;
      }
      try {
        let identity: string | null = null;
        if (cycleId) {
          // our own row coming back must not reset the edit script we are about to flush
          lastSavedRowIdRef.current = cycleId;
          const fresh = await refetchSupportingCycle();
          identity =
            (fresh.data?.data as { viciousCycleId?: string | null } | undefined)
              ?.viciousCycleId ?? null;
        }
        await Promise.all([refetchClientEra(), refetchReadyDomains()]);
        await saveViciousCycleEs(values, identity);
      } catch (retryError) {
        message.error(t('pages.supportingCycle.saveError'));
        Sentry.captureException(retryError);
      }
    }
  };

  useEffect(() => {
    if (upsertSupportingCycleData) {
      setWarnWhen(false);
      message.success(t('pages.supportingCycle.saveSuccess'));
    }

    // @ts-ignore
    if (upsertSupportingCycleData?.data?._id) {
      if (!cycleId) {
        // @ts-ignore
        setCycleId(upsertSupportingCycleData?.data?._id);
        therapistActivitySend({
          type: 'CREATED',
          // @ts-ignore
          resource: upsertSupportingCycleData?.data?._id,
        });
      } else {
        therapistActivitySend({
          type: 'UPDATED',
          // @ts-ignore
          resource: upsertSupportingCycleData?.data?._id,
        });
      }
    }
  }, [upsertSupportingCycleData]);

  useEffect(() => {
    const loadSupportingCycle = async () => {
      if (cycleId) {
        const res = await refetchSupportingCycle();
        const currentValues = formProps?.form?.getFieldsValue();

        if (res?.data?.data) {
          formProps?.form?.setFieldsValue({
            ...currentValues,
            // @ts-ignore
            ...(res?.data?.data || {}),
          });
        }
      }
    };
    loadSupportingCycle();
  }, [cycleId]);

  const saveData = useCallback(
    debounce(
      async (
        changedValues: Partial<typeof initialValues>,
        allValues: typeof initialValues,
      ) => {
        onFinish(allValues);
      },
      700,
    ),
    [],
  );

  const onValuesChange = (
    changedValues: Partial<typeof initialValues>,
    allValues: typeof initialValues,
  ) => {
    if (changedValues.name !== undefined) {
      recordOp({ kind: 'rename', name: (changedValues.name ?? '').trim() });
    }
    // antd reports a nested change as a SPARSE array: only the touched index is present
    const changed = changedValues.thoughts as
      | Array<
          { thought?: string; edges?: Array<boolean | undefined> } | undefined
        >
      | undefined;
    changed?.forEach((entry, index) => {
      if (!entry) return;
      if (entry.thought !== undefined) {
        recordOp({
          kind: 'edit',
          index,
          thought: (entry.thought ?? '').trim(),
        });
      }
      entry.edges?.forEach((active, to) => {
        if (active !== undefined) {
          recordOp({
            kind: 'toggle',
            from: index,
            to,
            active: Boolean(active),
          });
        }
      });
    });
    saveData(changedValues, allValues);
    formProps?.onValuesChange?.(changedValues, allValues);
  };

  return (
    <Create
      title={t('pages.supportingCycle.supportingCycles')}
      isLoading={
        clientsLoading ||
        supportingCycleLoading ||
        supportingCycleTemplatesLoading
      }
      saveButtonProps={{
        ...saveButtonProps,
        'loading': upsertSupportingCycleLoading || esLoading,
        'onClick': saveSupportingCycle,
        'data-testid': 'cc-vicious-cycles--save',
        'disabled': !clientId || !umlText,
      }}
    >
      <Form
        // @ts-ignore
        form={formProps?.form}
        name="supportingCycles"
        onFinish={onFinish}
        initialValues={initialValues}
        labelCol={{ span: 2 }}
        wrapperCol={{ span: 21 }}
        onValuesChange={onValuesChange}
        labelWrap
        data-testid="cc-create-vicious-cycles-form"
        data-era={isEsClient ? 'es' : 'legacy'}
      >
        <div className={styles['rowContainer']}>
          <SelectClient clientId={clientId} setClientId={setClientId} />
          {availableTemplates?.length ? (
            <Form.Item
              label={t('pages.supportingCycle.tempalteLabel')}
              style={{ marginLeft: 'auto' }}
              labelCol={{ span: 7 }}
            >
              <Select
                data-testid="cc-create-vicious-cycles-template-select"
                className={styles['startFromTemplateSelect']}
                showSearch
                value={selectedTemplate}
                filterOption={(input, option) =>
                  !!option?.label.toLowerCase().includes(input.toLowerCase())
                }
                onChange={(value) => {
                  setSelectedTemplate(value);
                  const template: SupportingCycle =
                    // @ts-ignore
                    supportingCycleTemplatesData?.data?.data.find(
                      // @ts-ignore
                      (t) => t._id === value,
                    );

                  recordOp({
                    kind: 'template',
                    templateId: String(value),
                    name: template.name ?? '',
                    thoughts: normalizeThoughts(template.thoughts),
                  });
                  const currentValues = formProps?.form?.getFieldsValue();
                  withoutRecording(() => {
                    formProps?.form?.setFieldsValue({
                      ...currentValues,
                      thoughts: template.thoughts,
                      name: template.name,
                    });
                  });

                  setTimeout(
                    () =>
                      // @ts-ignore
                      onFinish(formProps?.form?.getFieldsValue()),
                    10,
                  );
                }}
                options={availableTemplates}
                placeholder={t('pages.supportingCycle.selectTemplate')}
              />
            </Form.Item>
          ) : null}
        </div>
        <Form.Item
          label={t('pages.supportingCycle.cycleName')}
          name="name"
          className={styles['card-form-item']}
          labelCol={{ span: 2 }}
        >
          <Input
            placeholder={t('pages.supportingCycle.cycleName')}
            data-testid="cc-create-vicious-cycles-name-input"
          />
        </Form.Item>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <div>
            <Title
              level={5}
              style={{
                minWidth: 500,
              }}
            >
              {t('pages.supportingCycle.thoughts')}:
            </Title>
          </div>
        </div>
        <Form.List name="thoughts">
          {(fields, { add, remove }) => {
            return (
              <div>
                {fields.map(({ key, name, ...restField }) => {
                  return (
                    <div className={styles['thoughtRow']} key={key}>
                      <div className={styles['thoughtWithMinus']}>
                        {fields.length > 1 ? (
                          <MinusCircleOutlined
                            data-testid={`cc-create-vicious-cycles-thought-remove-${key}`}
                            style={{ margin: '5px 0 0 10px', flex: 1 }}
                            onClick={() => {
                              // @ts-ignore
                              const currentValues: typeof initialValues =
                                formProps?.form?.getFieldsValue() || {};
                              const thoughts = currentValues.thoughts || [];
                              const thoughtsBefore = thoughts.slice(0, key);
                              const thoughtsAfter = thoughts.slice(key + 1);

                              const thoughtsBeforeWithCorrectEdges =
                                thoughtsBefore.map((t) => {
                                  const edges = t.edges || [];
                                  const edgesBefore = edges.slice(0, key);
                                  const edgesAfter = edges.slice(key + 1);
                                  return {
                                    ...t,
                                    edges: [...edgesBefore, ...edgesAfter],
                                  };
                                });

                              const thoughtsAfterWithCorrectEdges =
                                thoughtsAfter.map((t, index) => {
                                  const edges = t.edges || [];
                                  const edgesBefore = edges.slice(0, key);
                                  const edgesAfter = edges.slice(key + 1);
                                  return {
                                    ...t,
                                    edges: [...edgesBefore, ...edgesAfter],
                                  };
                                });
                              recordOp({ kind: 'remove', index: name });
                              withoutRecording(() => {
                                remove(name);
                                // Note: This needs to be done in a timeout because otherwise
                                // the form will introduce a new value for edges and empty thought
                                setTimeout(() => {
                                  formProps?.form?.setFieldValue('thoughts', [
                                    ...thoughtsBeforeWithCorrectEdges,
                                    ...thoughtsAfterWithCorrectEdges,
                                  ]);
                                  const updatedValues =
                                    formProps?.form?.getFieldsValue();
                                  // @ts-ignore
                                  onFinish(updatedValues);
                                }, 10);
                              }, 120);
                            }}
                          />
                        ) : null}
                        <Form.Item
                          {...restField}
                          name={[name, 'thought']}
                          rules={[
                            { required: true, message: 'Missing thought' },
                          ]}
                          label={key + 1}
                          style={{ flex: 5 }}
                        >
                          <Input.TextArea
                            data-testid={`cc-create-vicious-cycles-thought-${key}`}
                            className={styles['thoughtRowInput']}
                            placeholder={t('pages.supportingCycle.thoughts')}
                            autoSize={{ minRows: 1, maxRows: 6 }}
                          />
                        </Form.Item>
                      </div>

                      <Form.Item
                        label={t('pages.supportingCycle.relations')}
                        style={{ flex: 5, minWidth: 320 }}
                        labelCol={{ span: 4 }}
                      >
                        <Form.List name={[name, 'edges']}>
                          {(edges, { add: addEdge, remove: removeEdge }) => {
                            return (
                              <Space
                                key={key}
                                style={{
                                  display: 'flex',
                                  marginBottom: 8,
                                }}
                                align="baseline"
                              >
                                {edges.map(({ key, name, ...restField }) => (
                                  <Tooltip
                                    key={`tooltip-${key}`}
                                    title={formProps?.form?.getFieldValue([
                                      'thoughts',
                                      key,
                                      'thought',
                                    ])}
                                  >
                                    <Form.Item
                                      {...restField}
                                      name={[name]}
                                      valuePropName="checked"
                                    >
                                      <Checkbox
                                        data-testid={`cc-create-vicious-cycles-thought-${key}-edge`}
                                      >
                                        {key + 1}
                                      </Checkbox>
                                    </Form.Item>
                                  </Tooltip>
                                ))}
                              </Space>
                            );
                          }}
                        </Form.List>
                      </Form.Item>
                    </div>
                  );
                })}
                <Form.Item>
                  <Button
                    data-testid="cc-create-vicious-cycles-thought-add"
                    id="cc-create-vicious-cycles-thought-add"
                    onClick={() => {
                      const values = formProps?.form?.getFieldsValue();

                      // @ts-ignore
                      const thoughtCount = values?.thoughts?.length || 0;
                      // @ts-ignore
                      const currentThoughts = values.thoughts;

                      recordOp({ kind: 'add', thought: '' });
                      withoutRecording(() => {
                        currentThoughts?.forEach((thought: any) => {
                          thought.edges.push(false);
                        });

                        formProps?.form?.setFieldValue(
                          'thoughts',
                          currentThoughts,
                        );

                        add({
                          thought: '',
                          edges: new Array(thoughtCount + 1).fill(false),
                        });
                      });
                    }}
                    icon={<PlusOutlined />}
                  >
                    {t('pages.supportingCycle.addField')}
                  </Button>
                </Form.Item>
              </div>
            );
          }}
        </Form.List>
        {umlText && (
          <DiagramImage
            testId="cc-create-vicious-cycles-uml"
            source={umlText}
            fallbackLines={[
              formProps?.form?.getFieldValue('name') ?? '',
              ...(formProps?.form?.getFieldValue('thoughts') ?? []).map(
                (thought: { thought?: string }) => thought?.thought ?? '',
              ),
            ]}
          />
        )}
      </Form>
    </Create>
  );
};
